import { Badge, Card, Group, Stack, Text, Title } from '@mantine/core';
import { useMemo } from 'react';
import {
  SessionGroupSessionsTable,
  buildGroupSessionHref,
  type GroupSessionRow,
} from '../../../../session-groups';
import type { SessionFullTractMetrics } from '../hooks/useSessionFullTractMetrics';
import type { SpectralWorkspaceSession } from '../types';
import { buildWorkspaceSessionRows } from '../utils/sortWorkspaceSessions';

type AddedSessionsPanelProps = {
  sessions: SpectralWorkspaceSession[];
  activeSessionGroupId?: string | null;
  analysisId?: string;
  isLoadingSessions?: boolean;
  sessionMetrics?: SessionFullTractMetrics[];
};

const buildLoadingPlaceholderRows = (count: number): GroupSessionRow[] =>
  Array.from({ length: count }, (_, index) => ({
    sessionId: -(index + 1),
    hrEventCount: -1,
    status: 'loading' as const,
  }));

const mapToGroupRows = (
  sessions: SpectralWorkspaceSession[],
  metricsBySessionId: Map<
    number,
    {
      meanRrMs: number | null;
      rmssdMs: number | null;
      lfHfRatio: number | null;
    }
  >
): GroupSessionRow[] =>
  buildWorkspaceSessionRows(sessions).map(session => {
    const tractMetrics = metricsBySessionId.get(session.sessionId);

    return {
      sessionId: session.sessionId,
      patientCode: session.patientCode,
      deviceName: session.deviceName,
      deviceSensorName: session.deviceSensorName,
      sessionDate:
        session.sessionDate ??
        (session.events?.[0]?.timestamp
          ? new Date(session.events[0].timestamp)
          : null),
      hasHrData:
        session.hasHrData ??
        (session.hrEventCount > 0 || (session.events?.length ?? 0) > 0),
      repairStatus: session.repairStatus,
      hrEventCount: session.hrEventCount,
      status: session.status,
      error: session.error,
      duplicateCount: session.duplicateCount,
      gapCount: session.gapCount,
      meanRrMs: tractMetrics?.meanRrMs ?? null,
      rmssdMs: tractMetrics?.rmssdMs ?? null,
      lfHfRatio: tractMetrics?.lfHfRatio ?? null,
    };
  });

export function AddedSessionsPanel({
  sessions,
  activeSessionGroupId,
  analysisId,
  isLoadingSessions = false,
  sessionMetrics = [],
}: AddedSessionsPanelProps) {
  const metricsBySessionId = useMemo(
    () =>
      new Map(
        sessionMetrics.map(metric => [
          metric.sessionId,
          {
            meanRrMs: metric.meanRrMs,
            rmssdMs: metric.rmssdMs,
            lfHfRatio: metric.lfHfRatio,
          },
        ])
      ),
    [sessionMetrics]
  );
  const rows = useMemo(
    () => mapToGroupRows(sessions, metricsBySessionId),
    [metricsBySessionId, sessionMetrics.length, sessions]
  );

  const getOpenHref = (sessionId: number) => {
    if (!activeSessionGroupId) {
      return '/sessions/group';
    }

    return buildGroupSessionHref(activeSessionGroupId, sessionId, analysisId);
  };

  if (sessions.length === 0) {
    if (activeSessionGroupId && isLoadingSessions) {
      return (
        <Card withBorder>
          <Stack gap='sm'>
            <div>
              <Title order={5}>Sessioni nel workspace</Title>
              <Text size='sm' c='dimmed' mt={4}>
                Caricamento sessioni del gruppo…
              </Text>
            </div>
            <SessionGroupSessionsTable
              rows={buildLoadingPlaceholderRows(6)}
              getOpenHref={() => '#'}
              showHrvMetrics
            />
          </Stack>
        </Card>
      );
    }

    return (
      <Card withBorder>
        <Text c='dimmed' size='sm'>
          Nessuna sessione nel workspace. Seleziona un gruppo con almeno una
          sessione per popolare l&apos;analisi.
        </Text>
      </Card>
    );
  }

  return (
    <Card withBorder>
      <Stack gap='sm'>
        <Group justify='space-between' align='flex-start' wrap='wrap'>
          <div>
            <Title order={5}>Sessioni nel workspace</Title>
            <Text size='sm' c='dimmed' mt={4}>
              La piattaforma calcola le metriche VFC (RR, RMSSD, LF/HF) sul
              tratto completo di ciascuna sessione.
            </Text>
          </div>
          <Badge variant='light'>{sessions.length} sessioni</Badge>
        </Group>

        <SessionGroupSessionsTable
          rows={rows}
          getOpenHref={getOpenHref}
          showHrvMetrics
          showDevice
          maxHeight={360}
        />
      </Stack>
    </Card>
  );
}
